import { font } from '@lemonade-hq/cdk';
import type { Locale } from '@lemonade-hq/lemonation';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import type React from 'react';
import { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { MapGroup, MapPolygon } from './MapPolygon';
import { translatedCountries } from './consts';
import type { Countries } from './consts';

const TOOLTIP_COLOR = '#282828';

const MapContainer = styled.div`
  position: relative;
  width: 100%;
`;

const MapTooltip = styled.div`
  text-transform: uppercase;
  position: absolute;
  background-color: ${TOOLTIP_COLOR};
  color: white;
  padding: 5px 10px;
  border-radius: 5px;
  z-index: 100;
  opacity: 0.95;
  white-space: nowrap;
  pointer-events: none;
  user-select: none;
  transform: translate(-50%, -100%);
  ${font('main', { fontSize: '1.6em', lineHeight: '1.2', fontWeight: 700 })};

  &:after {
    content: '';
    display: block;
    width: 15px;
    height: 15px;
    background-color: ${TOOLTIP_COLOR};
    transform: rotate(45deg);
    position: absolute;
    z-index: -1;
    top: 20px;
    left: calc(50% - 7px);
  }

  ${MEDIA_QUERIES.mobile`
      display: none
    `}
`;

interface RegionMapProps {
  regionCode: string;
  viewBox: string;
  polygons: Record<string, string | string[]>;
  highlightedPlaces: string[];
  tooltipTransform?: Record<string, { x: number; y: number }>;
  locale: Locale;
}

export const RegionMap: React.FC<RegionMapProps> = ({
  regionCode,
  viewBox,
  polygons,
  highlightedPlaces,
  tooltipTransform = {},
  locale,
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<
    | {
        x: number;
        y: number;
        text: string;
      }
    | undefined
  >(undefined);

  const hideTooltip = useCallback(() => setTooltip(undefined), []);

  const showTooltip = (ev: React.MouseEvent, code: string) => {
    const boundingBox = (ev.target as SVGGElement).getBoundingClientRect();
    const containerBoundingBox = mapContainerRef?.current?.getBoundingClientRect();
    const transform = tooltipTransform[code] || { x: 0, y: 0 };
    setTooltip({
      x: boundingBox.x - (containerBoundingBox?.x || 0) + boundingBox.width / 2 + transform.x,
      y: boundingBox.y - (containerBoundingBox?.y || 0) + boundingBox.height / 2 + transform.y,
      text: `${regionCode}.${code}`,
    });
  };

  return (
    <MapContainer ref={mapContainerRef}>
      <svg viewBox={viewBox}>
        {Object.keys(polygons).map(code => {
          const points = polygons[code];
          const pointsArray = Array.isArray(points) ? points : [points];

          return (
            <MapGroup
              key={code}
              code={code}
              highlighted={highlightedPlaces.includes(code)}
              onMouseIn={(ev: React.MouseEvent) => showTooltip(ev, code)}
              onMouseOut={hideTooltip}
            >
              {pointsArray.map((nestedPoints, index) => (
                <MapPolygon
                  key={
                    // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                    index
                  }
                  points={nestedPoints}
                />
              ))}
            </MapGroup>
          );
        })}
      </svg>

      {tooltip && (
        <MapTooltip style={{ left: tooltip.x, top: tooltip.y }}>
          {translatedCountries[locale][tooltip.text as Countries]}
        </MapTooltip>
      )}
    </MapContainer>
  );
};
